import { useEffect, useState } from "react";
import { orc } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Plus, Calculator, X, Trash2, Loader2, ArrowRight, DollarSign } from "lucide-react";
import { toast } from "sonner";

const UFS = ["SP", "RJ", "MG", "RS", "PR", "SC", "BA", "GO", "DF", "PE", "CE"];

export default function Orcamentos() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);

  const load = () => orc.list().then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const [form, setForm] = useState({ cliente_nome: "", uf: "SP", observacoes: "" });

  const criar = async () => {
    if (!form.cliente_nome) { toast.error("Nome do cliente é obrigatório"); return; }
    try {
      const { data } = await orc.criar(form);
      toast.success("Orçamento criado");
      setCreating(false); setForm({ cliente_nome: "", uf: "SP", observacoes: "" });
      load(); setOpen(data.id);
    } catch { toast.error("Erro"); }
  };

  if (open) return <OrcamentoDetalhe id={open} onClose={() => { setOpen(null); load(); }} />;

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-[#B28F4B] text-[10px] font-bold tracking-[0.3em] uppercase mb-3">Sistema SGO · Orçamentos</div>
            <h1 className="font-heading text-4xl md:text-5xl font-light text-white">
              Orçamentos <span className="text-[#B28F4B] italic font-serif">operacionais</span>
            </h1>
            <p className="text-[#A1A4C7] mt-2">CCT por cargo, cálculo de custos com encargos automáticos.</p>
          </div>
          <button
            onClick={() => setCreating(true)}
            data-testid="btn-novo-orcamento"
            className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-5 py-3 rounded-lg transition-colors"
          >
            <Plus size={16} /> Novo Orçamento
          </button>
        </div>

        <div className="grid gap-3">
          {items.length === 0 && (
            <div className="soeasy-card p-10 text-center">
              <Calculator size={40} className="mx-auto text-[#B28F4B] mb-2" strokeWidth={1.2}/>
              <div className="text-white font-heading">Nenhum orçamento ainda</div>
              <div className="text-[#A1A4C7] text-sm mt-1">Crie o primeiro orçamento</div>
            </div>
          )}
          {items.map(o => (
            <div
              key={o.id}
              onClick={() => setOpen(o.id)}
              data-testid={`orc-item-${o.id}`}
              className="soeasy-card p-5 cursor-pointer flex flex-col md:flex-row md:items-center gap-4 group"
            >
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold group-hover:text-[#B28F4B]">{o.cliente_nome}</div>
                <div className="text-xs text-[#A1A4C7] mt-1">
                  {o.uf} · {o.total_itens || 0} {(o.total_itens || 0) === 1 ? "item" : "itens"}
                  {o.observacoes && ` · ${o.observacoes}`}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-2xl font-light text-[#B28F4B]">
                  R$ {(o.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[9px] uppercase tracking-widest font-bold text-[#6B6E8F]">{o.status}</div>
              </div>
              <ArrowRight size={16} className="text-[#A1A4C7] group-hover:text-[#B28F4B] group-hover:translate-x-1 transition-all shrink-0"/>
            </div>
          ))}
        </div>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 fade-up" onClick={() => setCreating(false)}>
          <div onClick={e => e.stopPropagation()} className="soeasy-card p-8 max-w-md w-full">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl text-white">Novo Orçamento</h3>
              <button onClick={() => setCreating(false)}><X size={18} className="text-[#A1A4C7]"/></button>
            </div>
            <div className="space-y-3">
              <Input label="Cliente" value={form.cliente_nome} onChange={v => setForm({ ...form, cliente_nome: v })} testId="input-cliente-nome"/>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">UF</label>
                <select value={form.uf} onChange={e => setForm({ ...form, uf: e.target.value })} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none">
                  {UFS.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
              <Input label="Observações" value={form.observacoes} onChange={v => setForm({ ...form, observacoes: v })}/>
              <button onClick={criar} data-testid="btn-criar-orcamento" className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-3 rounded-lg mt-3">Criar</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function OrcamentoDetalhe({ id, onClose }) {
  const [o, setO] = useState(null);
  const [ccts, setCcts] = useState([]);
  const [cbos, setCbos] = useState([]);
  const [showItem, setShowItem] = useState(false);
  const [item, setItem] = useState(defaultItem());

  const load = () => orc.get(id).then(r => {
    setO(r.data);
    orc.ccts(r.data.uf).then(x => setCcts(x.data));
    orc.cbos().then(x => setCbos(x.data));
  });

  useEffect(() => { load(); }, [id]);

  const addItem = async () => {
    if (!item.cbo_id) { toast.error("Escolha um cargo"); return; }
    try {
      await orc.addItem({ ...item, orcamento_id: id });
      toast.success("Item adicionado");
      setShowItem(false); setItem(defaultItem());
      load();
    } catch { toast.error("Erro"); }
  };

  const delItem = async (iid) => {
    await orc.delItem(iid);
    load();
  };

  const preencherComCCT = (cctId) => {
    const cct = ccts.find(c => c.id === cctId);
    if (!cct || !cct.cargos || !cct.cargos[0]) return;
    const c = cct.cargos[0];
    setItem(prev => ({
      ...prev,
      cct_id: cctId,
      descricao_cargo: c.cbo_nome,
      piso_salarial: c.piso || 0,
      adic_noturno_pct: c.noturno_pct || 0,
      adic_periculosidade_pct: c.periculosidade_pct || 0,
      va_dia: c.va_dia || 0,
      vt_dia: c.vt_dia || 0,
    }));
  };

  if (!o) return <Shell><div className="p-10 text-center"><Loader2 className="animate-spin mx-auto text-[#B28F4B]"/></div></Shell>;

  return (
    <Shell>
      <div className="p-6 md:p-10 fade-up max-w-6xl mx-auto">
        <button onClick={onClose} className="text-[#B28F4B] text-sm font-semibold flex items-center gap-1 mb-5">← Voltar</button>

        <div className="soeasy-card p-6 md:p-8 mb-6" style={{ background: "linear-gradient(135deg, #1F2350 0%, #131532 100%)" }}>
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
            <div>
              <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#B28F4B] mb-2">Orçamento · {o.uf}</div>
              <h1 className="font-heading text-3xl font-medium text-white">{o.cliente_nome}</h1>
              {o.observacoes && <p className="text-[#A1A4C7] text-sm mt-1">{o.observacoes}</p>}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B6E8F]">Total Mensal</div>
              <div className="font-heading text-4xl font-light text-[#B28F4B]">
                R$ {(o.totais?.valor_total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-[#A1A4C7] mt-1">
                Base: R$ {(o.totais?.valor_base || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} + 8% encargos
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-end mb-4">
          <h2 className="font-heading text-xl text-white">Itens</h2>
          <button onClick={() => setShowItem(true)} data-testid="btn-add-item" className="inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold px-4 py-2 rounded-lg text-sm">
            <Plus size={14}/> Adicionar item
          </button>
        </div>

        <div className="grid gap-3">
          {(o.itens || []).length === 0 && <div className="soeasy-card p-8 text-center text-[#A1A4C7] text-sm">Nenhum item ainda</div>}
          {(o.itens || []).map(it => (
            <div key={it.id} className="soeasy-card p-5 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#B28F4B]/15 border border-[#B28F4B]/30 flex items-center justify-center">
                <DollarSign size={16} className="text-[#B28F4B]"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold">{it.descricao_cargo || "Cargo"}</div>
                <div className="text-xs text-[#A1A4C7]">
                  {it.quantidade}x · Turnos: {(it.turnos || []).join(", ") || "—"} · Piso R$ {it.piso_salarial}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading text-xl text-[#B28F4B]">R$ {(it.subtotal || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#6B6E8F] font-bold">Subtotal</div>
              </div>
              <button onClick={() => delItem(it.id)} className="text-[#A1A4C7] hover:text-red-400 p-2"><Trash2 size={16}/></button>
            </div>
          ))}
        </div>

        {showItem && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 fade-up" onClick={() => setShowItem(false)}>
            <div onClick={e => e.stopPropagation()} className="soeasy-card p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between mb-5">
                <h3 className="font-heading text-xl text-white">Adicionar Item</h3>
                <button onClick={() => setShowItem(false)}><X size={18} className="text-[#A1A4C7]"/></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">CCT (auto-preenche salário)</label>
                  <select onChange={e => preencherComCCT(e.target.value)} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 rounded-lg px-3 py-2.5 text-white text-sm">
                    <option value="">— Selecione uma CCT —</option>
                    {ccts.map(c => <option key={c.id} value={c.id}>{c.nome} ({c.sindicato})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">Cargo (CBO)</label>
                  <select value={item.cbo_id} onChange={e => setItem({ ...item, cbo_id: e.target.value })} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 rounded-lg px-3 py-2.5 text-white text-sm">
                    <option value="">— Selecione —</option>
                    {cbos.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <NumField label="Quantidade" value={item.quantidade} onChange={v => setItem({ ...item, quantidade: parseInt(v) || 1 })}/>
                  <NumField label="Piso R$" value={item.piso_salarial} onChange={v => setItem({ ...item, piso_salarial: parseFloat(v) || 0 })}/>
                  <NumField label="Noturno %" value={item.adic_noturno_pct} onChange={v => setItem({ ...item, adic_noturno_pct: parseFloat(v) || 0 })}/>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <NumField label="Periculosidade %" value={item.adic_periculosidade_pct} onChange={v => setItem({ ...item, adic_periculosidade_pct: parseFloat(v) || 0 })}/>
                  <NumField label="VA/dia" value={item.va_dia} onChange={v => setItem({ ...item, va_dia: parseFloat(v) || 0 })}/>
                  <NumField label="VT/dia" value={item.vt_dia} onChange={v => setItem({ ...item, vt_dia: parseFloat(v) || 0 })}/>
                  <NumField label="Conv Méd" value={item.convenio_medico_mes} onChange={v => setItem({ ...item, convenio_medico_mes: parseFloat(v) || 0 })}/>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">Turnos</label>
                  <div className="flex gap-2">
                    {["manha", "tarde", "noite"].map(t => (
                      <button
                        key={t}
                        onClick={() => setItem({ ...item, turnos: item.turnos.includes(t) ? item.turnos.filter(x => x !== t) : [...item.turnos, t] })}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase ${item.turnos.includes(t) ? "bg-[#B28F4B] text-[#1A1D42]" : "bg-[#131532] text-[#A1A4C7]"}`}
                      >{t}</button>
                    ))}
                  </div>
                </div>
                <button onClick={addItem} data-testid="btn-add-item-confirm" className="w-full bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold py-3 rounded-lg mt-3">Adicionar ao Orçamento</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

function defaultItem() {
  return {
    cbo_id: "", cct_id: "", descricao_cargo: "",
    quantidade: 1, turnos: ["manha"], piso_salarial: 0,
    adic_noturno_pct: 0, adic_periculosidade_pct: 0,
    va_dia: 0, vt_dia: 0, convenio_medico_mes: 0,
    cesta_basica_mes: 0, seguro_vida_mes: 0,
  };
}

function Input({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-2">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} data-testid={testId} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2.5 text-white text-sm outline-none"/>
    </div>
  );
}

function NumField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-widest text-[#B28F4B] block mb-1.5">{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)} className="w-full bg-[#0A0B1A] border border-[#B28F4B]/20 focus:border-[#B28F4B] rounded-lg px-3 py-2 text-white text-sm outline-none"/>
    </div>
  );
}
